// refreshAttributes — CM-22-01, the attribute-write seam 22 and 30 are blocked on (22 §2, §6.1).
//
// 07 owns the attribute READ: boot hydrates `workspaceStore.attributes` from the bootstrap
// `settings` section. Nothing re-read it afterwards, so every legacy write re-fetched the blob at
// its own call site (`workspaceStore.getWorkspaceAttributes(true)`). This is that re-read, owned
// once, here — 22's `SaveAttributeFlow` (and 30's ~20 keys) only trigger it.
//
// SERIALISED, and deliberately not by the obvious route. Sharing one in-flight promise across
// callers collapses N refetches into 1, but it is WRONG for the case it exists to serve: 30's
// `KeyEventsModal.vue:217-222` fires three writes at once, so writes 2 and 3 would join a fetch
// that was issued before their own write reached the server and the store would settle on stale
// data. So a caller arriving mid-flight is coalesced onto ONE trailing run instead — at most two
// requests for any burst, and the last one always starts after every write that asked for it.
// (The real fix is BE-22-01/02: have the write return the written row. Then this goes away.)
import { BootstrapService } from '../service/BootstrapService'
import { useAccountStore } from '../stores/accountStore'
import { useWorkspaceStore, type WorkspaceAttribute } from '../stores/workspaceStore'

export interface RefreshAttributesOptions {
  bootstrapService?: BootstrapService
}

let inFlight: Promise<boolean> | null = null
let trailing: Promise<boolean> | null = null

/** Re-hydrate `workspaceStore.attributes` for the SELECTED workspace. Resolves `true` when the
 *  store was rewritten; `false` when the refresh could not complete (no account/workspace in
 *  scope, the section failed, or the request threw). A `false` is the pane's "show the optimistic
 *  value + offer Retry" signal (22 §14) — a failed re-read never invalidates the write itself. */
export function refreshAttributes(options: RefreshAttributesOptions = {}): Promise<boolean> {
  if (!inFlight) return start(options)
  trailing ??= inFlight
    // The in-flight run's OUTCOME is irrelevant to the trailing one; only its completion matters.
    .catch(() => false)
    .then(() => {
      trailing = null
      return start(options)
    })
  return trailing
}

function start(options: RefreshAttributesOptions): Promise<boolean> {
  const run = execute(options).finally(() => {
    inFlight = null
  })
  inFlight = run
  return run
}

async function execute(options: RefreshAttributesOptions): Promise<boolean> {
  const accountStore = useAccountStore()
  const workspaceStore = useWorkspaceStore()

  const accountId = accountStore.accountId
  const workspaceId = workspaceStore.workspaceId
  if (accountId === undefined || workspaceId === undefined) return false

  const service = options.bootstrapService ?? new BootstrapService()
  try {
    const result = await service.bootSettingsOnly(accountId, workspaceId, { refresh: true })
    // `null` is a FAILED section (BE-ACC-06) — an empty workspace maps to `[]`. Writing `null`
    // through as `[]` would erase the blob on a partial-failure boot response.
    if (result.attributes === null) return false
    workspaceStore.setAttributes(result.attributes as WorkspaceAttribute[])
    return true
  } catch {
    // Every failure mode is the same to the caller: the write stands, the store did not move.
    // The pane owns the copy and the Retry (22 §14); a flow never toasts.
    return false
  }
}

/** Test seam: drops any in-flight/trailing run so cases do not leak coalescing state into
 *  each other. Production code never calls this. */
export function resetRefreshAttributesForTest(): void {
  inFlight = null
  trailing = null
}
